import { ISurvey, QuestionExpressionModel } from '@psyomics/surveyjs-core';
import { useMemo } from 'react';

import { useExpression } from '../../blocks/molecules/measure-breakdown/hooks/use-expression';
import {
  useAllRespondentTypes,
  useAnswersForRespondentType,
  useRenderContext,
  useSurveyForRespondentType,
} from '../../context/render-context';
import { Answers, Survey } from '../../survey-types';

import * as expressionUtils from './expressions';
import { IQuestionExpressionModelForMeasure, IQuestionExpressionModelForSubscale } from './expressions';

interface IsNotShownConditionProp {
  name: string;
  expression: string;
  page: {
    isVisible: boolean;
  };
}
const isNotShownCondition = (measureExpression: IsNotShownConditionProp): boolean => {
  const parentPage = measureExpression.page;
  return !parentPage.isVisible;
};

function extractFromCurlyBraces(str: string) {
  const regex = /{([^}]*)}/g;
  const matches = str.match(regex);

  if (!matches) return [];

  return matches.map((match) => match.slice(1, -1).trim());
}

function buildNestedExpressions(survey: ISurvey, expr: string, visited: Set<string> = new Set()): Set<string> {
  const questionSet = new Set<string>();
  const questionNames = extractFromCurlyBraces(expr);

  for (const questionName of questionNames) {
    if (visited.has(questionName)) {
      continue;
    }
    visited.add(questionName);

    const iQuestion = survey.getQuestionByName(questionName);

    if (!iQuestion) {
      continue;
    }

    if (iQuestion.getType() === 'expression') {
      const nestedQuestions = buildNestedExpressions(
        survey,
        (iQuestion as QuestionExpressionModel).expression,
        visited
      );
      nestedQuestions.forEach((q) => questionSet.add(q));
    } else {
      questionSet.add(questionName);
    }
  }
  return questionSet;
}

const isSkippedCondition = (expressionQuestion: { expression: string; survey: ISurvey }): boolean => {
  const questionNamesNeeded = Array.from(
    buildNestedExpressions(expressionQuestion.survey, expressionQuestion.expression)
  );

  const { survey } = expressionQuestion;

  return !questionNamesNeeded.every((qName) => {
    const test = survey.getQuestionByName(qName).isVisible;
    return test;
  });
};

enum AnsweredStatus {
  COMPLETED = 'COMPLETED',
  NOT_COMPLETED = 'NOT_COMPLETED',
  NOT_SHOWN = 'NOT_SHOWN',
  SKIPPED = 'SKIPPED',
}

type ConditionInfo = { answeredStatus: AnsweredStatus };

const getAnsweredStatus = (isCompleted: boolean, isSkipped: boolean, isShown: boolean) => {
  if (!isShown) return AnsweredStatus.NOT_SHOWN;
  if (!isCompleted) return AnsweredStatus.NOT_COMPLETED;
  if (isSkipped) return AnsweredStatus.SKIPPED;
  return AnsweredStatus.COMPLETED;
};

const getConditionInfo = (
  expressionQuestion?: IQuestionExpressionModelForMeasure | IQuestionExpressionModelForSubscale,
  answers?: Answers
): ConditionInfo => {
  const isCompleted = answers ? Object.keys(answers).length > 0 : false;

  if (!expressionQuestion)
    return {
      answeredStatus: getAnsweredStatus(isCompleted, false, false),
    };

  const isSkipped = isSkippedCondition(expressionQuestion);
  const notShown = isNotShownCondition(expressionQuestion);

  return {
    answeredStatus: getAnsweredStatus(isCompleted, isSkipped, !notShown),
  };
};
const getConditionInfoForSurvey = (survey: Survey, expressionName: string, answers?: Answers): ConditionInfo => {
  const expressionQuestion = expressionUtils.getExpressionFromSurvey(survey, expressionName);
  return getConditionInfo(expressionQuestion, answers);
};

const useConditions = (
  expressionQuestion?: IQuestionExpressionModelForMeasure | IQuestionExpressionModelForSubscale,
  answers?: Answers
): ConditionInfo => useMemo(() => getConditionInfo(expressionQuestion, answers), [answers, expressionQuestion]);

const useConditionForRespondentType = (respondentType: string, expressionName: string): ConditionInfo => {
  const survey = useSurveyForRespondentType(respondentType);
  const expression = useExpression({ survey, selectedExpressionName: expressionName });
  const answers = useAnswersForRespondentType(respondentType);

  return useMemo(() => getConditionInfo(expression, answers), [answers, expression]);
};

const useAllSurveyConditions = (expressionName?: string): Record<string, ConditionInfo | undefined> => {
  const respondentTypes = useAllRespondentTypes();
  const { surveySet, allAnswers } = useRenderContext();
  return useMemo(() => {
    return respondentTypes.reduce((acc, respondentType) => {
      const survey = surveySet?.find(({ respondentType: rt }) => rt === respondentType);
      return {
        ...acc,
        [respondentType]:
          survey && expressionName
            ? getConditionInfoForSurvey(survey, expressionName, allAnswers[respondentType])
            : undefined,
      };
    }, {});
  }, [allAnswers, expressionName, respondentTypes, surveySet]);
};

export {
  AnsweredStatus,
  buildNestedExpressions,
  extractFromCurlyBraces,
  isNotShownCondition,
  isSkippedCondition,
  useAllSurveyConditions,
  useConditionForRespondentType,
  useConditions,
};

export type { ConditionInfo, IsNotShownConditionProp };
